/**
 * Lebenssituation bearbeiten – eigenständige Tab-Seite
 */
import { useState, useEffect } from 'react';
import { Home, Shield, Heart, CreditCard, Euro, Check, Loader2, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';
import { WOHNFORM_KATALOG } from '@/lib/wohnform';

export default function LebenssituationPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};
  const klient = params.klient;

  const [saving, setSaving] = useState(false);
  const [jurPersonen, setJurPersonen] = useState([]);
  const [form, setForm] = useState({});

  useEffect(() => {
    betreuungAPI.listJuristischePersonen().then(setJurPersonen).catch(() => {});
    if (klient) {
      const a = typeof klient.adresse === 'object' ? klient.adresse : {};
      setForm({
        wohnform: klient.wohnform || '', pflegegrad: klient.pflegegrad || '',
        adresse: typeof klient.adresse === 'string' ? klient.adresse : (a.strasse || ''),
        plz: a.plz || klient.plz || '', ort: a.ort || klient.ort || '',
        krankenversicherung_id: klient.krankenversicherung_id || '',
        versichertennummer: klient.versichertennummer || '',
        kv_art: klient.kv_art || '', kv_beitragszahler: klient.kv_beitragszahler || '',
        zuzahlungsbefreiung: klient.zuzahlungsbefreiung || false,
        zuzahlungsbefreiung_bis: klient.zuzahlungsbefreiung_bis || '',
        zuzahlungsbefreiung_status: klient.zuzahlungsbefreiung_status || '',
        schwerbehinderung: klient.schwerbehinderung || false,
        grad_der_behinderung: klient.grad_der_behinderung || '',
        girokonto_bank: klient.girokonto_bank || '', girokonto_iban: klient.girokonto_iban || '',
        einkommensquelle: klient.einkommensquelle || '',
        betreuervergütung_staatskasse: klient.betreuervergütung_staatskasse || false,
        sozialamt_id: klient.sozialamt_id || '', grundsicherung_aktenzeichen: klient.grundsicherung_aktenzeichen || '',
        neue_kv_name: '', neue_bank_name: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNeueInstitution = async (fieldId, nameValue, typ, clearField) => {
    const name = (nameValue || '').trim();
    if (!name) { toast.error('Bitte Name eingeben'); return; }
    try {
      const result = await betreuungAPI.createJuristischePerson({ name, typ });
      setJurPersonen(prev => [...prev, result]);
      setForm(p => ({ ...p, [fieldId]: result.id, [clearField]: '', ...(fieldId === 'bank_institution_id' ? { girokonto_bank: name } : {}) }));
      toast.success(`${name} angelegt`);
    } catch { toast.error('Fehler'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { neue_kv_name, neue_bank_name, bank_institution_id, ...updateData } = form;
      if (bank_institution_id) {
        const bank = jurPersonen.find(j => j.id === bank_institution_id);
        if (bank) updateData.girokonto_bank = bank.name;
      }
      await betreuungAPI.updateKlient(params.klientId, updateData);
      // Auto-Massnahmen
      if (form.versichertennummer && form.krankenversicherung_id) {
        const kv = jurPersonen.find(j => j.id === form.krankenversicherung_id);
        await betreuungAPI.createMassnahme({ person_id: params.klientId, typ: 'krankenversicherung', bezeichnung: kv?.name || 'KV', aktenzeichen: form.versichertennummer, leistungsgeber_id: form.krankenversicherung_id, status: 'aktiv' }).catch(() => {});
      }
      if (form.zuzahlungsbefreiung && form.zuzahlungsbefreiung_bis) {
        await betreuungAPI.createMassnahme({ person_id: params.klientId, typ: 'zuzahlungsbefreiung', bezeichnung: 'Zuzahlungsbefreiung', gueltig_bis: form.zuzahlungsbefreiung_bis, status: 'aktiv' }).catch(() => {});
        const frist = new Date(form.zuzahlungsbefreiung_bis); frist.setDate(frist.getDate() - 28);
        await betreuungAPI.createAufgabe({ titel: 'Zuzahlungsbefreiung erneuern', klient_id: params.klientId, prioritaet: 'mittel', status: 'offen', faellig_am: frist.toISOString().slice(0, 10) }).catch(() => {});
      }
      if (form.schwerbehinderung && form.grad_der_behinderung) {
        await betreuungAPI.createMassnahme({ person_id: params.klientId, typ: 'schwerbehinderung', bezeichnung: `Schwerbehinderung GdB ${form.grad_der_behinderung}%`, status: 'aktiv' }).catch(() => {});
      }
      toast.success('Lebenssituation aktualisiert');
      emitDataRefresh('betreuungsakte');
      closeTab(activeTab.id);
    } catch { toast.error('Fehler beim Speichern'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="lebenssituation-page">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Lebenssituation bearbeiten</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>{klient?.vorname} {klient?.nachname}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Wohnsituation */}
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Home className="w-4 h-4" /> Wohnsituation</p>
            <Select value={form.wohnform || 'none'} onValueChange={v => setForm(p => ({ ...p, wohnform: v === 'none' ? '' : v }))}>
              <SelectTrigger><SelectValue placeholder="Auswählen..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nicht angegeben</SelectItem>
                {WOHNFORM_KATALOG.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Input value={form.adresse || ''} onChange={e => setForm(p => ({ ...p, adresse: e.target.value }))} placeholder="Strasse Nr." className="text-sm" />
              <Input value={form.plz || ''} onChange={e => setForm(p => ({ ...p, plz: e.target.value }))} placeholder="PLZ" className="text-sm" />
              <Input value={form.ort || ''} onChange={e => setForm(p => ({ ...p, ort: e.target.value }))} placeholder="Ort" className="text-sm" />
            </div>
          </div>

          {/* KV */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Shield className="w-4 h-4" /> Krankenversicherung</p>
            <div className="space-y-2">
              <Select value={form.krankenversicherung_id || 'none'} onValueChange={v => setForm(p => ({ ...p, krankenversicherung_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Versicherung..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">--</SelectItem>
                  {jurPersonen.filter(j => ['krankenversicherung', 'versicherung', 'sonstiges'].includes(j.typ)).map(j => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input value={form.neue_kv_name || ''} onChange={e => setForm(p => ({ ...p, neue_kv_name: e.target.value }))} placeholder="Neue Versicherung..." className="text-sm flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={() => handleNeueInstitution('krankenversicherung_id', form.neue_kv_name, 'krankenversicherung', 'neue_kv_name')} disabled={!(form.neue_kv_name || '').trim()}>
                  <Plus className="w-3 h-3 mr-1" /> Anlegen
                </Button>
              </div>
              <Input value={form.versichertennummer || ''} onChange={e => setForm(p => ({ ...p, versichertennummer: e.target.value }))} placeholder="Versichertennummer" className="text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.kv_art || 'none'} onValueChange={v => setForm(p => ({ ...p, kv_art: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="KV-Art..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">--</SelectItem><SelectItem value="gkv">GKV</SelectItem><SelectItem value="fgkv">Freiwillige GKV</SelectItem><SelectItem value="pkv">PKV</SelectItem><SelectItem value="familienversicherung">Familienvers.</SelectItem></SelectContent>
                </Select>
                <Select value={form.kv_beitragszahler || 'none'} onValueChange={v => setForm(p => ({ ...p, kv_beitragszahler: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Beitragszahler..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">--</SelectItem><SelectItem value="selbstzahler">Selbstzahler</SelectItem><SelectItem value="arbeitgeber">Arbeitgeber</SelectItem><SelectItem value="rentenversicherung">DRV</SelectItem><SelectItem value="jobcenter">Jobcenter</SelectItem></SelectContent>
                </Select>
              </div>
              <Select value={form.pflegegrad || 'none'} onValueChange={v => setForm(p => ({ ...p, pflegegrad: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Pflegegrad..." /></SelectTrigger>
                <SelectContent><SelectItem value="none">Keiner</SelectItem>{[1,2,3,4,5].map(g => <SelectItem key={g} value={String(g)}>Pflegegrad {g}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.zuzahlungsbefreiung || false} onChange={e => setForm(p => ({ ...p, zuzahlungsbefreiung: e.target.checked }))} id="zuzahl" className="rounded" />
                <Label htmlFor="zuzahl" className="cursor-pointer text-xs">Zuzahlungsbefreiung</Label>
              </div>
              {form.zuzahlungsbefreiung && (
                <div className="ml-6 bg-slate-50 rounded-lg p-3 space-y-2">
                  <Select value={form.zuzahlungsbefreiung_status || 'none'} onValueChange={v => setForm(p => ({ ...p, zuzahlungsbefreiung_status: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Status..." /></SelectTrigger>
                    <SelectContent><SelectItem value="none">--</SelectItem><SelectItem value="befreit">Befreit</SelectItem><SelectItem value="nicht_befreit">Nicht befreit</SelectItem><SelectItem value="beantragt">Beantragt</SelectItem></SelectContent>
                  </Select>
                  <Input type="date" value={form.zuzahlungsbefreiung_bis || ''} onChange={e => setForm(p => ({ ...p, zuzahlungsbefreiung_bis: e.target.value }))} className="text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Schwerbehinderung */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Heart className="w-4 h-4" /> Schwerbehinderung</p>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.schwerbehinderung || false} onChange={e => setForm(p => ({ ...p, schwerbehinderung: e.target.checked }))} id="schwb" className="rounded" />
              <Label htmlFor="schwb" className="cursor-pointer text-sm">Schwerbehinderung liegt vor</Label>
            </div>
            {form.schwerbehinderung && (
              <div className="ml-6 mt-2 bg-slate-50 rounded-lg p-3">
                <Label className="text-xs">GdB (%)</Label>
                <Input value={form.grad_der_behinderung || ''} onChange={e => setForm(p => ({ ...p, grad_der_behinderung: e.target.value }))} placeholder="z.B. 50" className="text-sm w-32 mt-1" />
              </div>
            )}
          </div>

          {/* Bankverbindung */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Bankverbindung</p>
            <div className="space-y-2">
              <Select value={form.bank_institution_id || 'none'} onValueChange={v => setForm(p => ({ ...p, bank_institution_id: v === 'none' ? '' : v, girokonto_bank: jurPersonen.find(j => j.id === v)?.name || p.girokonto_bank }))}>
                <SelectTrigger><SelectValue placeholder="Bank..." /></SelectTrigger>
                <SelectContent><SelectItem value="none">--</SelectItem>{jurPersonen.filter(j => ['bank','sonstiges'].includes(j.typ)).map(j => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input value={form.neue_bank_name || ''} onChange={e => setForm(p => ({ ...p, neue_bank_name: e.target.value }))} placeholder="Neue Bank anlegen..." className="text-sm flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={() => handleNeueInstitution('bank_institution_id', form.neue_bank_name, 'bank', 'neue_bank_name')} disabled={!(form.neue_bank_name || '').trim()}>
                  <Plus className="w-3 h-3 mr-1" /> Anlegen
                </Button>
              </div>
              <Input value={form.girokonto_iban || ''} onChange={e => setForm(p => ({ ...p, girokonto_iban: e.target.value }))} placeholder="IBAN: DE..." className="text-sm font-mono" />
            </div>
          </div>

          {/* Versorgung */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Euro className="w-4 h-4" /> Versorgung / Einkommen</p>
            <div className="space-y-2">
              <Select value={form.einkommensquelle || 'none'} onValueChange={v => setForm(p => ({ ...p, einkommensquelle: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Einkommensquelle..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nicht angegeben</SelectItem><SelectItem value="rente">Rente</SelectItem>
                  <SelectItem value="grundsicherung">Grundsicherung</SelectItem><SelectItem value="buergergeld">Bürgergeld</SelectItem>
                  <SelectItem value="erwerbseinkommen">Erwerbseinkommen</SelectItem><SelectItem value="vermoegen">Vermögen</SelectItem>
                  <SelectItem value="sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.betreuervergütung_staatskasse || false} onChange={e => setForm(p => ({ ...p, betreuervergütung_staatskasse: e.target.checked }))} id="staatskasse" className="rounded" />
                <Label htmlFor="staatskasse" className="cursor-pointer text-sm">Vergütung aus Staatskasse</Label>
              </div>
            </div>
          </div>

          {/* Aktionen */}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-lebenssituation">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Check className="w-4 h-4 mr-2" />Speichern</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
